/**
 * 見積書（PC）の表の 規格・仕様／備考 の幅（#140、docs/47 §15・docs/10 §9.1）。
 *   ・人ごと（個人設定 estimateColumns＝PUT /account/preferences。drawerHand と同じ器）。新しい見積・別の PC でも同じ幅
 *   ・ドラッグの間は画面の中だけで変え、変え終わって 500ms 後に1回だけ保存する（閉じる・移るときに残っていればすぐ送る）
 *   ・上限は contract の ESTIMATE_COLUMN_LIMITS（1366px で社内用の列を出しても表が横に流れない）。片方を広げたら、もう片方の分を差し引いた幅まで
 *   ・見積依頼書の表（which="rfq"）は別の設定 rfqColumns（#220。10/20 に見積書と分けた）。無ければ見積書の幅に従う＝既定は見積書と同じ。
 *     「既定幅に復元」は見積依頼書の設定を消す（見積書の幅に戻る）
 */
import { clampEstimateColumns, ESTIMATE_COLUMN_LIMITS, type AccountPreferences, type EstimateColumns, type Me } from "@genbaryoku/contract";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { api } from "../../api.js";
import { ME_KEY, useMe } from "../../auth.js";
import { DEFAULT_WIDTHS, type SizableKey } from "./columns.js";

export type ColPreset = "narrow" | "normal" | "wide";
export const presetWidth = (key: SizableKey, p: ColPreset): number => {
  const L = ESTIMATE_COLUMN_LIMITS[key];
  return p === "narrow" ? L.min : p === "wide" ? L.wide : L.def;
};
/** 表の見出しに渡すもの */
export type ColSizer = {
  widths: EstimateColumns;
  /** ドラッグ・矢印キー：その場で変える（保存は 500ms 後） */
  set: (key: SizableKey, px: number) => void;
  preset: (key: SizableKey, p: ColPreset) => void;
  /** 既定に戻す（2つとも。人の設定を消す） */
  reset: () => void;
};

export function useEstimateColumns(which: "estimate" | "rfq" = "estimate"): ColSizer {
  const me = useMe();
  const qc = useQueryClient();
  const prefs = me.data?.preferences;
  const saved = (which === "rfq" ? (prefs?.rfqColumns ?? prefs?.estimateColumns) : prefs?.estimateColumns) ?? null;
  const field = which === "rfq" ? "rfqColumns" : "estimateColumns";
  const [local, setLocal] = useState<EstimateColumns | null | undefined>(undefined);
  // 人の設定を消したあと（local＝null）：見積書は既定の幅、見積依頼書は見積書の幅
  const fallback = which === "rfq" ? (prefs?.estimateColumns ?? null) : null;
  const widths = (local === undefined ? saved : (local ?? fallback)) ?? DEFAULT_WIDTHS;
  /** 送っていない値（undefined＝なし。null＝既定に戻す） */
  const pending = useRef<EstimateColumns | null | undefined>(undefined);
  const timer = useRef<number | undefined>(undefined);
  const send = () => {
    window.clearTimeout(timer.current);
    const v = pending.current;
    if (v === undefined) return;
    pending.current = undefined;
    const prev = qc.getQueryData<Me>(ME_KEY);
    if (prev) qc.setQueryData<Me>(ME_KEY, { ...prev, preferences: { ...prev.preferences, [field]: v } });
    api<AccountPreferences>("PUT", "/account/preferences", { [field]: v }).catch((e: unknown) => console.warn("列の幅を保存できませんでした", e));
  };
  const sendRef = useRef(send);
  sendRef.current = send;
  useEffect(() => () => sendRef.current(), []); // 画面を離れるときに残っていれば送る
  const put = (v: EstimateColumns | null) => {
    setLocal(v);
    pending.current = v;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => sendRef.current(), 500);
  };
  return {
    widths,
    set: (key, px) => put(clampEstimateColumns({ ...widths, [key]: px }, key)),
    preset: (key, p) => put(clampEstimateColumns({ ...widths, [key]: presetWidth(key, p) }, key)),
    reset: () => put(null),
  };
}
